import { useState } from 'react';
import { Carte, Chargement, MessageErreur, Pagination, Pastille } from '../ui/Primitives';
import { ChampFiltre } from '../ui/Onglets';
import { ErreurApi } from '../../lib/api';
import { useTaillePage } from '../../lib/useTaillePage';
import { useHistoriqueParametrage } from '../../lib/useParametrage';

function horodatage(instant: string): string {
  return new Date(instant).toLocaleString('fr-FR', {
    timeZone: 'Indian/Reunion',
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

/**
 * Historique des réglages (TF-435) : chaque modification tracée, avec son
 * auteur et ce qu'elle a changé. Le journal complet de la plateforme est
 * l'objet du journal d'audit (TF-438).
 */
export function OngletHistorique(): JSX.Element {
  const [action, setAction] = useState('');
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();
  const { data, isLoading, error } = useHistoriqueParametrage({ action, page, taille: taillePage });

  return (
    <Carte
      titre="Historique des réglages"
      sansBordure
      actions={
        <ChampFiltre
          etiquette="Réglage"
          valeur={action}
          onChange={(valeur) => {
            setAction(valeur);
            setPage(1);
          }}
        >
          <option value="">Tous les réglages</option>
          {(data?.actions ?? []).map((choix) => (
            <option key={choix.code} value={choix.code}>
              {choix.libelle}
            </option>
          ))}
        </ChampFiltre>
      }
    >
      {isLoading && <Chargement />}
      {error && (
        <div className="p-4 sm:p-6">
          <MessageErreur message={error instanceof ErreurApi ? error.message : 'L’historique n’a pas pu être chargé.'} />
        </div>
      )}
      {data && data.lignes.length === 0 && (
        <p className="px-4 py-10 text-center text-sm text-codialis-gray sm:px-6">Aucun réglage tracé.</p>
      )}
      <ul className="divide-y divide-gray-100">
        {data?.lignes.map((ligne) => (
          <li key={ligne.id} className="px-4 py-3 sm:px-6">
            <div className="flex flex-wrap items-center gap-2">
              <Pastille texte={ligne.actionLibelle} ton="info" />
              {ligne.objet && <span className="text-sm font-semibold text-codialis-navy">{ligne.objet}</span>}
            </div>
            <div className="mt-0.5 text-[11px] text-codialis-gray">
              {horodatage(ligne.survenuLe)} · {ligne.auteur ?? 'Compte supprimé'}
            </div>
            {ligne.changements.length > 0 ? (
              <ul className="mt-1.5 space-y-0.5 text-xs text-codialis-navy">
                {ligne.changements.map((changement) => (
                  <li key={changement.champ} className="break-words">
                    <span className="font-semibold">{changement.champ}</span> :{' '}
                    <span className="text-codialis-gray line-through">{changement.avant}</span> → {changement.apres}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mt-1 text-[11px] text-codialis-gray">Réglage tracé sans le détail de ce qui a changé.</div>
            )}
          </li>
        ))}
      </ul>
      {data && data.pagination.total > 0 && (
        <Pagination
          page={data.pagination.page}
          pages={data.pagination.pages}
          total={data.pagination.total}
          affiches={data.lignes.length}
          taillePage={taillePage}
          onPage={setPage}
          onChangerTaille={(taille) => {
            setTaillePage(taille);
            setPage(1);
          }}
          uniteLabel="réglage"
        />
      )}
    </Carte>
  );
}
